/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
  Form, Input, Row, Col, Modal, DatePicker, Spin,
  Divider,
} from 'antd';
import {
  DATE_FORMAT,
} from 'utils/formatters';
import { patchGenerarDeclaracion, postDeclaracionJuegos, postRechazarDeclaracion } from 'api/recaudacion';
import { SyncOutlined } from '@ant-design/icons';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import _logger from 'utils/logger';
import propTypes from 'prop-types';
import moment from 'moment';
import { RFC_PATTERN, CURP_PATTERN } from 'utils/patterns';
import { toMoment } from 'utils/normalizers';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC valido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP valida.' }],
};

const FormJuegos = ({
  padron,
  statusDeclaracion,
  visible,
  setVisibleForm,
  isComplementaria,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);

  const handleCancel = async () => {
    setLoading(true);
    await postRechazarDeclaracion({
      declaracion: declaracionId,
      canal_de_pago: 5,
    });
    setLoading(false);
  };

  const handleBeforeUnload = () => {
    handleCancel();
  };

  const handleUnload = (event) => {
    const message = 'Se perderán los datos capturados, ¿Estás seguro de salir?';
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = message; // Esto mostrará el cuadro de confirmación
    return message;
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (visible) {
      window.addEventListener('unload', handleUnload);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('unload', handleUnload);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [visible]);

  // Resetea el formulario cuando se cierra.
  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible]);

  function redondear(numero) {
    const decimal = numero - Math.floor(numero);

    if (decimal >= 0.51) {
      return Math.ceil(numero);
    }
    return Math.floor(numero);
  }

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }
      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo: padron?.empresa?.contribuyente?.razon_social || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        entidad: 1,
        fecha_del_pago_realizado_anterior: isComplementaria && !isZeroState ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          impuesto_determinado: 0,
          impuesto1_de_juegos_y_apuestas: 0,
          impuesto2_de_rifas_loterias_y_sorteos: 0,
          impuesto3_de_premios_obtenidos: 0,
          impuesto4_de_erogaciones_por_participar: 0,
          base1_de_juegos_y_apuestas: 0,
          base2_de_rifas_loterias_y_sorteos: 0,
          base3_de_premios_obtenidos: 0,
          base4_de_erogaciones_por_participar: 0,
          monto_pagado_con_anterioridad: 0,
          actualizacion: 0,
          multas: 0,
          recargos: 0,
          retenciones: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionJuegos(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validacionesDeOperaciones = (values) => {
    const retencion_rounded = parseFloat(redondear(values.retenciones)).toFixed(2);
    const impuesto_rounded = parseFloat(redondear(values.impuesto)).toFixed(2);

    // ImpuestosCorrespondiente menos Retenciones = ImpuestoCargo(debe ser mayor igual a cero)
    if ((parseFloat(impuesto_rounded) - parseFloat((retencion_rounded))) < 0 && !isZeroState) {
      form.validateFields(['impuesto_a_cargo']).then(() => {
        form.setFields([
          {
            name: 'impuesto_a_cargo',
            errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
          },
        ]);
      });
      form.validateFields(['retenciones']).then(() => {
        form.setFields([
          {
            name: 'retenciones',
            errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
          },
        ]);
      });
    } else if ((parseFloat(impuesto_rounded) - parseFloat((retencion_rounded))) >= 0 && !isZeroState) {
      form.validateFields(['impuesto_a_cargo']).then(() => {
        form.setFields([
          {
            name: 'impuesto_a_cargo',
            errors: [],
          },
        ]);
      });
      form.validateFields(['retenciones']).then(() => {
        form.setFields([
          {
            name: 'retenciones',
            errors: [],
          },
        ]);
      });
    }

    if ((parseFloat(impuesto_rounded) - parseFloat((retencion_rounded))) <= 0 && !isZeroState) {
      form.setFieldsValue({ actualizacion: 0, recargos: 0, multas: 0 });
    }
  };

  // Calcula el total de la cantidad a pagar.
  const total = () => {
    const values = form.getFieldsValue();
    const actualizacion = parseFloat(values.actualizacion) || 0;
    const recargos = parseFloat(values.recargos) || 0;
    const multas = parseFloat(values.multas) || 0;
    const monto_pagado_con_anterioridad = parseFloat(values.monto_pagado_con_anterioridad) || 0;

    const impuesto1 = parseInt(values.impuesto1_de_juegos_y_apuestas, 10) || 0;
    const impuesto2 = parseInt(values.impuesto2_de_rifas_loterias_y_sorteos, 10) || 0;
    const impuesto3 = parseInt(values.impuesto3_de_premios_obtenidos, 10) || 0;
    const impuesto4 = parseInt(values.impuesto4_de_erogaciones_por_participar, 10) || 0;

    const impuesto_determinado_total = [
      impuesto1,
      impuesto2,
      impuesto3,
      impuesto4,
    ].reduce((a, b) => a + b, 0);

    const total_de_contribuciones = [
      impuesto_determinado_total,
      actualizacion,
      recargos,
      multas,
    ].reduce((a, b) => parseFloat(a) + parseFloat(b), 0).toFixed(2);

    const cantidad_a_cargo = parseFloat(total_de_contribuciones - monto_pagado_con_anterioridad).toFixed(2);

    const cantidad_a_pagar_rounded = redondear(parseFloat(cantidad_a_cargo).toFixed(2));
    const total_de_contribuciones_rounded = redondear(parseFloat(total_de_contribuciones).toFixed(2));

    form.setFieldsValue({
      impuesto_determinado: parseFloat(impuesto_determinado_total).toFixed(2),
      total_de_contribuciones: parseFloat(total_de_contribuciones_rounded).toFixed(2),
    });

    const isNegative = parseFloat(total_de_contribuciones).toFixed(2) < monto_pagado_con_anterioridad;

    if (isNegative) {
      const total_a_favor_rounded = redondear(parseFloat((monto_pagado_con_anterioridad - total_de_contribuciones)).toFixed(2));
      form.setFieldsValue({
        cantidad_a_favor: parseFloat(total_a_favor_rounded).toFixed(2),
        cantidad_a_cargo: parseFloat(0).toFixed(2),
      });
    } else {
      form.setFieldsValue({
        cantidad_a_cargo: parseFloat(cantidad_a_pagar_rounded).toFixed(2),
        cantidad_a_favor: parseFloat(0).toFixed(2),
      });
    }

    validacionesDeOperaciones(values);
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              IMPUESTO SOBRE JUEGOS Y APUESTAS PERMITIDAS, SOBRE RIFAS, LOTERÍAS Y SORTEOS.
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
              >
                Cancelar
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                Generar
              </Button>
            </Col>
          </Row>
      )}
        width="35vw"
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{
              fecha_del_pago_anterior: moment(),
              es_declaracion_en_ceros: false,
              impuesto1_de_juegos_y_apuestas: 0,
              impuesto2_de_rifas_loterias_y_sorteos: 0,
              impuesto3_de_premios_obtenidos: 0,
              impuesto4_de_erogaciones_por_participar: 0,
              base1_de_juegos_y_apuestas: 0,
              base2_de_rifas_loterias_y_sorteos: 0,
              base3_de_premios_obtenidos: 0,
              base4_de_erogaciones_por_participar: 0,
              actualizacion: 0,
              recargos: 0,
              multas: 0,
              cantidad_a_cargo: 0,
              cantidad_a_favor: 0,
              total_de_contribuciones: 0,
              impuesto_determinado: 0,
              monto_pagado_con_anterioridad: isComplementaria ? 0 : 0,
            }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10}>
              { isZeroState ? (
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24} sm={24} md={24}>
                  <table style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>Concepto</th>
                        <th>Monto</th>
                      </tr>
                      <tr>
                        <td>
                          Juegos y apuestas permitidas; importe total de los billetes y demás
                          <br />
                          comprobantes que permitan participar en las apuestas que se crucen
                        </td>
                        <td>
                          <Form.Item
                            hasFeedback
                            tooltip="prueba"
                            name="base1_de_juegos_y_apuestas"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input
                              prefix="$"
                              onBlur={(val) => {
                                const result = val.target.value * 0.04;
                                form.setFieldsValue({ impuesto1_de_juegos_y_apuestas: parseInt(result, 10) });
                                total();
                              }}
                              onChange={(value) => {
                                if (value.target.value === '') {
                                  form.setFieldsValue({ impuesto1_de_juegos_y_apuestas: 0 });
                                } else {
                                  const val_monto = parseFloat(value.target.value).toFixed(2) * 0.04;
                                  form.setFieldsValue({ impuesto1_de_juegos_y_apuestas: parseInt(val_monto, 10) });
                                }
                                total();
                              }}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Impuesto correspondiente</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto1_de_juegos_y_apuestas"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Rifas, loterías y sorteos; Importe total de los boletos vendidos</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="base2_de_rifas_loterias_y_sorteos"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input
                              prefix="$"
                              onBlur={(val) => {
                                const result = val.target.value * 0.08;
                                form.setFieldsValue({ impuesto2_de_rifas_loterias_y_sorteos: parseInt(result, 10) });
                                total();
                              }}
                              onChange={(value) => {
                                if (value.target.value === '') {
                                  form.setFieldsValue({ impuesto2_de_rifas_loterias_y_sorteos: 0 });
                                } else {
                                  const val_monto = parseFloat(value.target.value) * 0.08;
                                  form.setFieldsValue({ impuesto2_de_rifas_loterias_y_sorteos: parseInt(val_monto, 10) });
                                }
                                total();
                              }}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Impuesto correspondiente</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto2_de_rifas_loterias_y_sorteos"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Monto de los premios obtenidos</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="base3_de_premios_obtenidos"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input
                              prefix="$"
                              onBlur={(val) => {
                                const result = val.target.value * 0.06;
                                form.setFieldsValue({ impuesto3_de_premios_obtenidos: parseInt(result, 10) });
                                total();
                              }}
                              onChange={(value) => {
                                if (value.target.value === '') {
                                  form.setFieldsValue({ impuesto3_de_premios_obtenidos: 0 });
                                } else {
                                  const val_monto = parseFloat(value.target.value) * 0.06;
                                  form.setFieldsValue({ impuesto3_de_premios_obtenidos: parseInt(val_monto, 10) });
                                }
                                total();
                              }}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Impuesto correspondiente</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto3_de_premios_obtenidos"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          Erogaciones para participar en juegos con apuestas; importe total
                          <br />
                          de las cantidades que se entreguen por concepto de acceso con
                          <br />
                          y utilización de máquinas o instalaciones relacionadas
                          <br />
                          juegos de apuestas, cargas y recargas adicionales mediante
                          <br />
                          tarjetas,
                          bandas magnéticas, dispositivos electrónicos, etc.
                        </td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="base4_de_erogaciones_por_participar"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input
                              prefix="$"
                              onBlur={(val) => {
                                const result = val.target.value * 0.10;
                                form.setFieldsValue({ impuesto4_de_erogaciones_por_participar: parseInt(result, 10) });
                                total();
                              }}
                              onChange={(value) => {
                                if (value.target.value === '') {
                                  form.setFieldsValue({ impuesto4_de_erogaciones_por_participar: 0 });
                                } else {
                                  const val_monto = parseFloat(value.target.value) * 0.10;
                                  form.setFieldsValue({ impuesto4_de_erogaciones_por_participar: parseInt(val_monto, 10) });
                                }
                                total();
                              }}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Impuesto correspondiente</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto4_de_erogaciones_por_participar"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <Divider />
                      <tr>
                        <td>Impuesto determinado</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto_determinado"
                            id="impuesto_determinado"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Actualización (Monto manual)</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="actualizacion"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input
                              prefix="$"
                              onChange={() => total()}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Recargos (Monto manual)</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="recargos"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input
                              prefix="$"
                              onChange={() => total()}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Multas (Monto manual)</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="multas"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input
                              prefix="$"
                              onChange={() => total()}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Total de contribuciones</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="total_de_contribuciones"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      {
                        isComplementaria && (
                          <>
                            <tr>
                              <td>Fecha del pago anterior</td>
                              <td>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  rules={[_rules.required('El campo es requerido')]}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td>Monto pagado con anterioridad (Monto manual)</td>
                              <td>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El campo es requerido'),
                                    ..._rules.numeric,
                                    // ..._rules.noMayorAImpustoCorrespondiente,
                                  ]}
                                >
                                  <Input prefix="$" onChange={() => { total(); }} />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )
                      }
                      <tr>
                        <td>Cantidad a pagar</td>
                        <td>
                          <Form.Item
                            name="cantidad_a_cargo"
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Cantidad a favor</td>
                        <td>
                          <Form.Item
                            name="cantidad_a_favor"
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                    </thead>
                  </table>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormJuegos.propTypes = {
  padron: propTypes.shape({
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,

  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  callbackCancel: propTypes.func,
  declaracionId: propTypes.func.isRequired,
};

FormJuegos.defaultProps = {
  isComplementaria: false,
  visible: false,
  callbackCancel: () => {},
};

export default FormJuegos;
